import { Box } from "@mui/material";
import { useState, useEffect } from "react";
import { ShippingGroupPreview } from "../ShippingGroupPreview";

import { useFastCheckoutContext } from "../../../CheckoutMachine";

import { ChevronDownIcon, MOBILE_SCREEN_SIZE } from "@brandclub/common-ui";
import {
  ShippingGroup,
  ShoppingCartItem,
  UserInteractionType,
} from "../../../types";
import { SummaryProduct } from "../../SummaryProductV2";
import { useSelector } from "@xstate/react";
import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";
import ButtonContainer from "../components/Buttons/ButtonContainer";
import SaveButton from "../components/Buttons/SaveButton";

export const getErrorProducts = (items: ShoppingCartItem[]) => {
  let res: ShoppingCartItem[] = items;
  res = res.filter(
    (item) => item.status === "error" && item?.userApprovalStatus !== "deleted"
  );
  return res;
};

const ErrorProducts = ({
  errorProducts,
}: {
  errorProducts: ShoppingCartItem[];
}) => {
  const { send, actorRef } = useFastCheckoutContext();
  const [outOfStockProduct, setOutOfStockProduct] =
    useState<ShoppingCartItem[]>(errorProducts);

  const onRemove = (sku: string) => {
    setOutOfStockProduct(
      outOfStockProduct.filter((e) => e.stacklineSku !== sku)
    );
    if (outOfStockProduct.filter((e) => e.stacklineSku !== sku).length === 0) {
      // removed all, now should call confirm to continue order
      send({
        type: "SUBMIT_USER_INTERACTION",
        data: {
          interactionType: UserInteractionType.INVENTORY_CONFIRMATION,
          interactionData: {
            approve: true,
          },
        },
      });
    }
  };

  const retailerCheckoutOrderStatus = useSelector(
    actorRef,
    (snapshot) => snapshot.context.retailerCheckoutOrderStatus
  );

  const userInteractionRequirement =
    retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
      ?.userInteractionRequirement;
  const disabled =
    userInteractionRequirement?.type ===
    UserInteractionType.NO_INVENTORY_AVAILABLE;

  return (
    <>
      {outOfStockProduct.map((errorProduct) => {
        return (
          <SummaryProduct
            product={errorProduct}
            key={errorProduct.stacklineSku}
            onRemove={onRemove}
            disabled={disabled}
          />
        );
      })}
    </>
  );
};

const DeliverySection = () => {
  const { actorRef, send } = useFastCheckoutContext();

  const deliverySectionState = useSelector(
    actorRef,
    (state) => state.context.sectionState.delivery
  );

  const { open, chevronShow, errorMessage } = deliverySectionState;

  const toggleOpen = () => {
    if (open) {
      send({ type: "ON_SESSION_CLOSE", data: { sessionToClose: "delivery" } });
    } else {
      send({ type: "ON_SESSION_OPEN", data: { sessionToOpen: "delivery" } });
    }
  };

  const userRetailerCheckoutOrder = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
  );

  const userInteractionRequirement =
    userRetailerCheckoutOrder?.userInteractionRequirement;

  const shippingGroups =
    userRetailerCheckoutOrder?.shippingOptions?.shippingGroups;
  const [openOptions, setOpenOptions] = useState<boolean[]>([]);

  useEffect(() => {
    const length = shippingGroups?.length ?? 0;
    setOpenOptions(new Array(length).fill(open));
  }, [open, shippingGroups?.length]);

  const items = userRetailerCheckoutOrder?.items;

  const errorProducts = getErrorProducts(items ?? []);

  const [shippingGroupsClone, setShippingGroupsClone] = useState<
    ShippingGroup[]
  >(shippingGroups ?? []);

  // use a local clone of the shippingGroups and update the selected shipping option
  useEffect(() => {
    setShippingGroupsClone(shippingGroups ?? []);
  }, [shippingGroups]);

  const handleUpdateShippingOption = async (
    index: number,
    deliveryOption?: {
      name: string;
      cssSelector: string;
    },
    deliveryTimeWindow?: {
      name: string;
      cssSelector: string;
    }
  ) => {
    try {
      const updatedShippingGroups = shippingGroupsClone.map((e, i) => {
        if (index === i) {
          const deliveryOptions = e.deliveryOptions;
          const updatedDeliveryOptions = deliveryOptions.map((option) => {
            if (
              option.name === deliveryOption?.name &&
              option.cssSelector === deliveryOption?.cssSelector
            ) {
              if (deliveryTimeWindow) {
                option.deliveryTimeWindows = option.deliveryTimeWindows.map(
                  (window) => {
                    if (
                      window.name === deliveryTimeWindow?.name &&
                      window.cssSelector === deliveryTimeWindow?.cssSelector
                    ) {
                      return { ...window, isSelected: true };
                    }
                    return { ...window, isSelected: false };
                  }
                );
              }
              return { ...option, isSelected: true };
            }
            return { ...option, isSelected: false };
          });
          return { ...e, deliveryOptions: updatedDeliveryOptions };
        }
        return e;
      });
      console.log("updatedShippingGroups", updatedShippingGroups);
      setShippingGroupsClone(updatedShippingGroups);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveShippingOption = () => {
    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.SHIPPING_OPTION_SELECTION,
        interactionData: {
          shippingOption: {
            shippingGroups: shippingGroupsClone,
          },
        },
      },
    });
  };

  return (
    <Box className="section">
      <div className="section_head" style={{ cursor: "pointer" }}>
        <div className="title">Delivery</div>
        {chevronShow ? (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "33px",
              height: "33px",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
            onClick={toggleOpen}
          ></ChevronDownIcon>
        ) : null}
      </div>
      <div>
        <StoreErrorV2
          errorMessage={errorMessage}
          sx={{
            width: "fit-content",
            marginTop: "2.2px",
            marginBottom: "24px",
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              marginTop: "0px",
              marginBottom: "15px",
            },
          }}
        />
        <div className="shipping_group_list">
          {errorProducts.length > 0 ? (
            <ErrorProducts errorProducts={errorProducts} />
          ) : null}
          {shippingGroupsClone?.map((e, index) => {
            return (
              <ShippingGroupPreview
                key={index}
                shippingGroup={e}
                index={index}
                handleUpdateShippingOption={(
                  deliveryOption,
                  deliveryTimeWindow
                ) =>
                  handleUpdateShippingOption(
                    index,
                    deliveryOption,
                    deliveryTimeWindow
                  )
                }
                deliveryOpen={openOptions}
                openItem={(index: number) => {
                  const newOpenOptions = [...openOptions];
                  newOpenOptions[index] = !newOpenOptions[index];
                  setOpenOptions(newOpenOptions);
                }}
              />
            );
          })}
          {!shippingGroups && !errorProducts?.length ? (
            <>
              {items?.map((item) => {
                return (
                  <SummaryProduct product={item} key={item.stacklineSku} />
                );
              })}
            </>
          ) : null}
          {userInteractionRequirement?.type ===
          UserInteractionType.SHIPPING_OPTION_SELECTION ? (
            <ButtonContainer
              sx={{
                alignSelf: "flex-end",
                justifyContent: "flex-end",
              }}
            >
              {/* <ClearButton>Clear</ClearButton> */}
              <SaveButton onClick={handleSaveShippingOption}>Save</SaveButton>
            </ButtonContainer>
          ) : null}
        </div>
      </div>
    </Box>
  );
};

export default DeliverySection;
